import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './CategoryOptionsList.module.scss';
import { CategoryCheckbox } from './CategoryCheckbox';
import { CategoryInput } from './CategoryInput';
import { ReactComponent as IconPlus } from './img/iconPlus.svg';
import { Loader } from '@mantine/core';
import ContentLoader from 'react-content-loader';

interface Option {
  value: string;
  label: string;
}

interface CategoryOptionsListProps {
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  searchPlaceholder?: string;
  setIsCreatedModalOpen: (value: boolean) => void;
  setIsСategoryOpen: (value: boolean) => void;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  selectedSummaryLabels?: string[];
  isFetchingList?: boolean;
}

export const CategoryOptionsList: FC<CategoryOptionsListProps> = ({
  options,
  selectedValues,
  onChange,
  setIsCreatedModalOpen,
  setIsСategoryOpen,
  searchPlaceholder = 'Искать...',
  hasNextPage = false,
  isFetchingMore = false,
  onLoadMore,
  isLoading = false,
  searchValue: controlledSearch,
  onSearchChange,
  selectedSummaryLabels,
  isFetchingList = false,
}) => {
  const [localSearch, setLocalSearch] = useState('');
  const isServerSearch = controlledSearch !== undefined && onSearchChange !== undefined;
  const searchValue = isServerSearch ? controlledSearch : localSearch;
  const setSearchValue = isServerSearch ? onSearchChange : setLocalSearch;

  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreLockRef = useRef(false);

  useEffect(() => {
    if (!isFetchingMore) {
      loadMoreLockRef.current = false;
    }
  }, [isFetchingMore]);

  const handleLoadMore = useCallback(() => {
    if (!onLoadMore || !hasNextPage || isFetchingMore || loadMoreLockRef.current) {
      return;
    }
    loadMoreLockRef.current = true;
    onLoadMore();
  }, [onLoadMore, hasNextPage, isFetchingMore]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight) return;
    const gapToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = gapToBottom < 72 && el.scrollTop > 12;
    if (nearBottom) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  const getServiceTypeSummary = (): string[] => {
    if (!options || options.length === 0) return [];

    const byValue = new Map<string, string>();
    options.forEach((o) => byValue.set(o.value, o.label));

    const labels = selectedValues
      .map((v) => byValue.get(v))
      .filter((l): l is string => Boolean(l));

    return labels;
  };

  const summary = selectedSummaryLabels ?? getServiceTypeSummary();

  const displayedSearchValue = searchValue;

  const filteredOptions = useMemo(() => {
    if (isServerSearch) return options;
    const valueForFilter = searchValue.trim().toLowerCase();
    if (!valueForFilter) return options;
    return options.filter(
      (option) => option.label && option.label.toLowerCase().includes(valueForFilter),
    );
  }, [options, searchValue, isServerSearch]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
  }, [filteredOptions, hasNextPage, isFetchingMore]);

  const singleSelect = false;

  const handleOptionChange =
    (optionValue: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (singleSelect) {
        if (e.target.checked) {
          onChange([optionValue]);
        } else {
          onChange([]);
        }
      } else {
        if (e.target.checked) {
          onChange([...selectedValues, optionValue]);
        } else {
          const newValues = selectedValues.filter(
            (value) => String(value) !== String(optionValue),
          );
          onChange(newValues);
        }
      }
    };

  return (
    <div>
      <div className={styles.searchContainer}>
        <CategoryInput
          type='search'
          value={displayedSearchValue}
          onChange={(val) => {
            setSearchValue(String(val));
          }}
          placeholder={searchPlaceholder}
          summary={summary}
        />
      </div>

      <div className={styles.btnContainer}>
        <button
          className={styles.btn}
          onClick={() => {
            setIsCreatedModalOpen(true);
            setIsСategoryOpen(false);
          }}>
          <IconPlus /> Новая категория
        </button>
      </div>

      <div ref={scrollRef} className={styles.scrollableContainer} onScroll={handleScroll}>
        {isLoading ? (
          <ContentLoader
            speed={2}
            height={'270px'}
            width='100%'
            className={styles.tab_loader}
            backgroundColor={'#FFFFFF'}
            foregroundColor={'#D8E4FB8C'}>
            <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
          </ContentLoader>
        ) : isFetchingList ? (
          <div className={styles.loadMoreRow}>
            <Loader size='xs' color='#002CFB' />
          </div>
        ) : (
          <div className={styles.options}>
            {filteredOptions.length === 0 && (
              <div className={styles.noOptions}>Ничего не найдено</div>
            )}

            {filteredOptions.map((option) => {
              const stringValue = String(option.value);
              return (
                <div key={stringValue} className={styles.option}>
                  <CategoryCheckbox
                    checked={selectedValues.includes(stringValue)}
                    onChange={handleOptionChange(stringValue)}
                    label={option.label}
                  />
                </div>
              );
            })}
          </div>
        )}
        {isFetchingMore && hasNextPage && (
          <div className={styles.loadMoreRow}>
            <Loader size='xs' color='#002CFB' />
          </div>
        )}
      </div>
    </div>
  );
};
